import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, ZoomControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './MapCard.css';

// Fix default marker icon issue with Leaflet in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

export const MapCard = ({ adresse, ville, codePostal, pays, nom, prenom }) => {
  const [coordinates, setCoordinates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [foundAddress, setFoundAddress] = useState(null);

  useEffect(() => {
    const geocodeAddress = async () => {
      if (!ville && !adresse) {
        setError('Adresse non disponible');
        setLoading(false);
        return;
      }

      try {
        // Utilise l'API Adresse de data.gouv.fr (BAN - Base Adresse Nationale)
        // Plus précis pour les adresses françaises, 100% gratuit, sans clé API
        
        const searchQueries = [
          // Essai avec adresse complète
          [adresse, codePostal, ville].filter(Boolean).join(' '),
          // Essai avec code postal et ville
          [codePostal, ville].filter(Boolean).join(' '),
          // Essai avec juste la ville
          ville
        ].filter(q => q && q.trim());

        let foundCoords = null;
        let addressLabel = null;

        for (const query of searchQueries) {
          if (!query) continue;

          try {
            // API Adresse data.gouv.fr
            const response = await fetch(
              `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=1`
            );

            const data = await response.json();

            if (data && data.features && data.features.length > 0) {
              const feature = data.features[0];
              const [lng, lat] = feature.geometry.coordinates;
              
              foundCoords = { lat, lng };
              addressLabel = feature.properties.label;
              break; // Succès, on arrête
            }
          } catch (err) {
            console.error(`Échec géocodage avec: ${query}`, err);
            continue; // Essayer la stratégie suivante
          }
        }

        if (foundCoords) {
          setCoordinates(foundCoords);
          setFoundAddress(addressLabel);
          setError(null);
        } else {
          setError('Adresse introuvable sur la carte');
        }
      } catch (err) {
        console.error('Erreur de géocodage:', err);
        setError('Erreur lors du chargement de la carte');
      } finally {
        setLoading(false);
      }
    };

    geocodeAddress();
  }, [adresse, ville, codePostal, pays]);

  if (loading) {
    return (
      <div className="w-full h-64 bg-muted rounded-md flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-sm text-muted-foreground">Chargement de la carte...</p>
        </div>
      </div>
    );
  }

  if (error || !coordinates) {
    return (
      <div className="w-full h-64 bg-muted rounded-md flex items-center justify-center">
        <p className="text-sm text-muted-foreground">{error || 'Carte non disponible'}</p>
      </div>
    );
  }

  return (
    <div className="w-full h-80 rounded-md overflow-hidden border border-border">
      <MapContainer
        center={[coordinates.lat, coordinates.lng]}
        zoom={15}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
        zoomControl={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <ZoomControl position="topright" />
        <Marker position={[coordinates.lat, coordinates.lng]}>
          <Popup>
            <div className="text-center">
              <p className="font-semibold">{prenom} {nom}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {foundAddress || (
                  <>
                    {adresse}<br />
                    {codePostal} {ville}
                  </>
                )}
              </p>
            </div>
          </Popup>
        </Marker>
      </MapContainer>
    </div>
  );
};

export default MapCard;
